import { useEffect, useId, useRef, useState } from "react";

interface ImpactInfoTooltipProps {
  label: string;
  definition: string;
}

export default function ImpactInfoTooltip({ label, definition }: ImpactInfoTooltipProps) {
  const tooltipId = useId();
  const containerRef = useRef<HTMLSpanElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const isOpen = !isDismissed && (isHovered || isFocused || isPinned);

  useEffect(() => {
    if (!isOpen) return;

    const dismissOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsPinned(false);
        setIsDismissed(true);
      }
    };
    const dismissWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPinned(false);
        setIsDismissed(true);
      }
    };
    document.addEventListener("pointerdown", dismissOutside);
    document.addEventListener("keydown", dismissWithEscape);
    return () => {
      document.removeEventListener("pointerdown", dismissOutside);
      document.removeEventListener("keydown", dismissWithEscape);
    };
  }, [isOpen]);

  return (
    <span
      ref={containerRef}
      className={`impact-info${isOpen ? " impact-info--open" : ""}`}
      onMouseEnter={() => {
        setIsHovered(true);
        setIsDismissed(false);
      }}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => {
        setIsFocused(true);
        setIsDismissed(false);
      }}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setIsFocused(false);
      }}
    >
      <button
        type="button"
        className="impact-info__button"
        aria-label={`About ${label}`}
        aria-describedby={tooltipId}
        aria-expanded={isOpen}
        onClick={() => {
          setIsPinned((pinned) => {
            setIsDismissed(pinned);
            return !pinned;
          });
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6.5" />
          <path d="M8 7.1v4M8 4.8h.01" />
        </svg>
      </button>
      <span id={tooltipId} className="impact-info__tooltip" role="tooltip" aria-hidden={!isOpen}>
        {definition}
      </span>
    </span>
  );
}
